import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { useState, useEffect, useRef } from "react";
import { TourOverlay } from "@/components/tour-overlay";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { TourWithSteps } from "@shared/schema";
import { Loader2 } from "lucide-react";

export default function TourPlayer() {
  const [, params] = useRoute("/tour/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const tourId = params?.id;

  // Parse restart query param
  const urlParams = new URLSearchParams(window.location.search);
  const shouldRestart = urlParams.get("restart") === "true";

  const { data: tour, isLoading } = useQuery<TourWithSteps>({
    queryKey: ["/api/tours", tourId],
    enabled: !!tourId,
  });

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // Maintain authoritative local state for completed steps to avoid stale data
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
  // Track if restart has been handled to prevent infinite loop
  const hasHandledRestart = useRef(false);

  // Initialize step from progress or restart
  useEffect(() => {
    if (!tour) return;

    if (shouldRestart && !hasHandledRestart.current) {
      hasHandledRestart.current = true;
      setCurrentStepIndex(0);
      setCompletedStepIds([]);
      // Reset progress on server
      const firstStepId = tour.steps[0]?.id;
      updateProgressMutation.mutate({
        currentStepId: firstStepId || null,
        completedSteps: [],
        status: "in_progress",
      });
      // Clear the restart param to avoid re-triggering
      window.history.replaceState({}, "", `/tour/${tourId}`);
    } else if (!shouldRestart && tour.progress) {
      // Find the index of the current step
      if (tour.progress.currentStepId) {
        const stepIndex = tour.steps.findIndex(s => s.id === tour.progress?.currentStepId);
        if (stepIndex >= 0) {
          setCurrentStepIndex(stepIndex);
        }
      }
      // Initialize completed steps from server
      const existingCompleted = Array.isArray(tour.progress.completedSteps)
        ? (tour.progress.completedSteps as string[])
        : [];
      setCompletedStepIds(existingCompleted);
    }
  }, [tour, shouldRestart, tourId]);

  const updateProgressMutation = useMutation({
    mutationFn: async (data: {
      currentStepId: string | null;
      completedSteps: string[];
      status: "not_started" | "in_progress" | "completed";
    }) => {
      const response = await fetch(`/api/tours/${tourId}/progress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to update progress");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/tours", tourId] });
      queryClient.invalidateQueries({ queryKey: ["/api/tours"] });
    },
  });

  const handleNext = () => {
    if (!tour) return;
    const nextIndex = currentStepIndex + 1;

    if (nextIndex < tour.steps.length) {
      setCurrentStepIndex(nextIndex);

      // Update local completed steps state
      const currentStep = tour.steps[currentStepIndex];
      const updatedCompleted = [...completedStepIds];
      if (!updatedCompleted.includes(currentStep.id)) {
        updatedCompleted.push(currentStep.id);
      }
      setCompletedStepIds(updatedCompleted);

      // Update progress on server
      updateProgressMutation.mutate({
        currentStepId: tour.steps[nextIndex].id,
        completedSteps: updatedCompleted,
        status: "in_progress",
      });
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);

      // Use local state for completed steps
      updateProgressMutation.mutate({
        currentStepId: tour!.steps[prevIndex].id,
        completedSteps: completedStepIds,
        status: "in_progress",
      });
    }
  };

  const handleComplete = () => {
    if (!tour) return;

    // Mark all steps as completed
    const allStepIds = tour.steps.map(s => s.id);

    updateProgressMutation.mutate(
      {
        currentStepId: tour.steps[tour.steps.length - 1].id,
        completedSteps: allStepIds,
        status: "completed",
      },
      {
        onSuccess: () => {
          toast({
            title: "Tour completed! 🎉",
            description: `You've finished "${tour.title}"`,
          });
          setLocation("/tours");
        },
      }
    );
  };

  const handleClose = () => {
    // Save current progress before closing
    if (tour && currentStepIndex >= 0) {
      const currentStep = tour.steps[currentStepIndex];

      // Use local state for completed steps
      updateProgressMutation.mutate({
        currentStepId: currentStep?.id || null,
        completedSteps: completedStepIds,
        status: currentStepIndex > 0 || completedStepIds.length > 0 ? "in_progress" : "not_started",
      });
    }

    setLocation("/tours");
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Tour not found</h2>
          <button
            onClick={() => setLocation("/tours")}
            className="text-primary hover:underline"
          >
            Return to tours
          </button>
        </div>
      </div>
    );
  }

  return (
    <TourOverlay
      tourId={tour.id}
      tourTitle={tour.title}
      steps={tour.steps}
      currentStepIndex={currentStepIndex}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onClose={handleClose}
      onComplete={handleComplete}
    />
  );
}
